/**
 * @audit GAP-FE-B005 · raw HTTP retained — see `shared/services/AUDIT_HTTP_DEBT.md`
 *
 * Severity: MEDIUM
 * Reason:  low-level WS framework primitive — not an API surface (acceptable)
 *
 * When this service no longer holds `inject(HttpClient)` or hand-rolled
 * `this.http.X` calls (either by migrating to `@api` or sequestering
 * to a `*-bridge.service.ts`), remove this banner and the matching row
 * in `AUDIT_HTTP_DEBT.md`.
 */
import {
  Injectable,
  inject,
  OnDestroy,
  signal,
  computed,
  DestroyRef,
} from "@angular/core";
import { HttpClient } from '@angular/common/http';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Observable,
  Subject,
  BehaviorSubject,
  timer,
  EMPTY,
  throwError,
  merge,
} from "rxjs";
import {
  switchMap,
  catchError,
  tap,
  takeUntil,
  map,
  distinctUntilChanged,
  retry,
  share,
  finalize,
} from "rxjs/operators";
import {
  WebSocketService,
  WebSocketMessage,
  WebSocketConfig,
  WebSocketLifecycle,
} from "./websocket.service";
import { LoggerService } from './logger.service';
import { AuthService } from './auth.service';
import {
  SubscriptionIntent,
  updateSubscriptionIntents,
} from "./websocket-subscription-intents";

/**
 * Connection state with detailed information
 */
export interface WebSocketConnectionState {
  status:
    | "disconnected"
    | "connecting"
    | "connected"
    | "reconnecting"
    | "error"
    | "degraded";
  lastConnected: Date | null;
  lastDisconnected: Date | null;
  reconnectAttempt: number;
  maxReconnectAttempts: number;
  error: string | null;
  usingPollingFallback: boolean;
  queuedSubscriptions: number;
}

/**
 * Queued subscription waiting for connection
 */
interface QueuedSubscription {
  endpoint: string;
  payload: unknown;
  timestamp: Date;
  retryCount: number;
}

/**
 * Polling fallback configuration
 */
export interface PollingFallbackConfig {
  enabled: boolean;
  endpoint: string | (() => string);
  intervalMs: number;
  maxRetries: number;
}

/**
 * WebSocket Resilience Service
 *
 * Provides robust WebSocket connection handling with:
 * - Connection state tracking across all endpoints
 * - Subscription queuing when disconnected
 * - Automatic replay of subscriptions on reconnection
 * - Polling fallback when WebSocket unavailable
 * - Global connection status for UI indicators
 *
 * Sentry Errors Addressed:
 * - ADMIN-INTERFACE-18: Trading health WebSocket error
 * - ADMIN-INTERFACE-7: WebSocket disconnected
 * - ADMIN-INTERFACE-13: WebSocket connection failed
 * - ADMIN-INTERFACE-E: Cannot subscribe: WebSocket not connected
 */
@Injectable({
  providedIn: "root",
})
export class WebSocketResilienceService implements OnDestroy {
  private readonly wsService = inject(WebSocketService);
  private readonly logger = inject(LoggerService);
  private readonly authService = inject(AuthService);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  // Connection states per endpoint
  private readonly endpointStates = new Map<string, BehaviorSubject<WebSocketConnectionState>>();

  // Global connection state (aggregate of all endpoints)
  private readonly _globalState = signal<WebSocketConnectionState>({
    status: "disconnected",
    lastConnected: null,
    lastDisconnected: null,
    reconnectAttempt: 0,
    maxReconnectAttempts: 10,
    error: null,
    usingPollingFallback: false,
    queuedSubscriptions: 0,
  });
  readonly globalState = this._globalState.asReadonly();

  // Derived signals for easy UI binding
  readonly isConnected = computed(
    () => this._globalState().status === "connected",
  );
  readonly isConnecting = computed(
    () =>
      this._globalState().status === "connecting" ||
      this._globalState().status === "reconnecting",
  );
  readonly hasError = computed(
    () =>
      this._globalState().status === "error" ||
      this._globalState().status === "degraded",
  );
  readonly isDegraded = computed(
    () => this._globalState().usingPollingFallback,
  );
  readonly queuedCount = computed(
    () => this._globalState().queuedSubscriptions,
  );

  // Observable version for components preferring RxJS
  readonly globalState$ = toObservable(this._globalState).pipe(
    distinctUntilChanged(
      (a, b) =>
        a.status === b.status && a.reconnectAttempt === b.reconnectAttempt,
    ),
  );

  // Subscription queue
  private readonly subscriptionQueue = new Map<string, QueuedSubscription[]>();

  // Polling fallback subjects
  private readonly pollingFallbacks = new Map<string, Subject<void>>();
  private readonly pollingData$ = new Map<
    string,
    Observable<WebSocketMessage>
  >();
  private readonly endpointStops = new Map<string, Subject<void>>();
  private readonly endpointOwners = new Map<string, number>();

  // Event emitters for UI notifications
  private readonly connectionEvents$ = new Subject<{
    type: 'connected' | 'disconnected' | 'error' | 'reconnecting' | 'degraded';
    endpoint: string;
    message: string;
  }>();

  readonly events$ = this.connectionEvents$.asObservable();

  constructor() {
    // Subscribe to core WebSocket service status changes
    this.wsService.connectionStatus$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status) => {
        if (this.endpointStates.size === 0) {
          this.updateGlobalStateFromCoreStatus(status);
        } else {
          this.updateGlobalState();
        }
      });

    // Subscribe to auth changes to handle re-authentication

    this.authService.isAuthenticated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isAuth) => {
        if (isAuth) {
          this.processQueuedSubscriptions();
        } else {
          for (const endpoint of this.endpointStops.keys()) {
            this.stopEndpoint(endpoint);
          }
          this.subscriptionQueue.clear();
          this.updateQueuedCount();
        }
      });
  }

  ngOnDestroy(): void {
    for (const endpoint of this.endpointStops.keys()) {
      this.stopEndpoint(endpoint);
    }
    this.pollingFallbacks.forEach((subject) => {
      subject.next();
      subject.complete();
    });
    this.endpointStates.forEach((subject) => subject.complete());
  }

  /**
   * Connect to a WebSocket endpoint with resilience features.
   *
   * GAP-FE-RESILIENCE (#30): this is the canonical connect path for every
   * real-time feature feed. Feature services MUST route their connect through
   * here (rather than calling `wsService.connect()` directly) so that:
   *   - a WS error is caught and, when `pollingFallback.enabled`, degrades to
   *     HTTP polling against `pollingFallback.endpoint` (an /api/v1/* REST path)
   *     instead of failing hard;
   *   - the endpoint + global connection state flips to `'degraded'` /
   *     `usingPollingFallback = true`, which the navbar `app-global-websocket-status`
   *     pill and each surface's `app-connection-status` render for free.
   *
   * `connectConfig` is forwarded verbatim to the single underlying
   * `wsService.connect(endpoint, connectConfig)` call so the live socket keeps
   * the feature's reconnect/heartbeat configuration — there is exactly one
   * socket per endpoint (no double-subscription).
   */
  connectWithResilience(
    endpoint: string,
    pollingFallback?: PollingFallbackConfig,
    connectConfig?: Partial<WebSocketConfig>,
  ): Observable<WebSocketMessage> {
    this.initializeEndpointState(endpoint);
    this.endpointOwners.set(
      endpoint,
      (this.endpointOwners.get(endpoint) ?? 0) + 1,
    );
    let stop = this.endpointStops.get(endpoint);
    if (!stop) {
      stop = new Subject<void>();
      this.endpointStops.set(endpoint, stop);
    }
    const stopped$ = stop;
    const live$ = this.wsService
      .connect(endpoint, {
        ...connectConfig,
        enablePollingFallback: pollingFallback?.enabled ?? false,
      })
      .pipe(
        catchError((error) => {
          this.onConnectionError(endpoint, error);
          return pollingFallback?.enabled
            ? this.startPollingFallback(endpoint, pollingFallback)
            : throwError(() => error);
        }),
      );
    const fallback$ = this.wsService.getConnectionLifecycle$(endpoint).pipe(
      tap((lifecycle) => this.syncEndpointState(endpoint, lifecycle)),
      map((lifecycle) =>
        Boolean(
          pollingFallback?.enabled &&
          (lifecycle.retriesExhausted || this.pollingFallbacks.has(endpoint)) &&
          !lifecycle.healthy,
        ),
      ),
      distinctUntilChanged(),
      switchMap((polling) =>
        polling && pollingFallback
          ? this.startPollingFallback(endpoint, pollingFallback)
          : EMPTY,
      ),
    );
    // The lifecycle branch survives browser onerror/onclose. HTTP delivery never
    // replaces the live message stream, so a healthy recovery can cancel polling.
    return merge(live$, fallback$).pipe(
      takeUntil(stopped$),
      takeUntilDestroyed(this.destroyRef),
      share(),
    );
  }

  /**
   * Queue a subscription for when connection is available
   * Returns immediately if connected, otherwise queues for later
   */
  queueSubscription(endpoint: string, payload: unknown): boolean {
    // Market selections have a persistent core-owned desired set, including
    // offline removals; a one-shot queue would lose those cancellations.
    if (endpoint === 'market-data') { return this.wsService.send(endpoint, payload); }
    const state = this.getEndpointState(endpoint);
    if (state.value.status === "connected") {
      return this.wsService.send(endpoint, payload);
    }
    const previous = this.subscriptionQueue.get(endpoint) ?? [];
    const intents = updateSubscriptionIntents(
      previous.map((item) => item.payload as SubscriptionIntent),
      payload,
    );
    this.subscriptionQueue.set(
      endpoint,
      intents.map((intent) => ({
        endpoint,
        payload: intent,
        timestamp: new Date(),
        retryCount: 0,
      })),
    );
    if (intents.length) {
      this.logger.info(`Queued subscription for ${endpoint} - will send when connected`, { queueSize: intents.length });
    }
    this.updateQueuedCount();
    return false;
  }

  /**
   * Send a message, queuing if not connected
   */
  sendWithQueue(endpoint: string, message: unknown): boolean {
    return this.queueSubscription(endpoint, message);
  }

  /**
   * Get connection state for a specific endpoint
   */
  getEndpointConnectionState(
    endpoint: string,
  ): Observable<WebSocketConnectionState> {
    this.initializeEndpointState(endpoint);
    return this.getEndpointState(endpoint).asObservable();
  }

  /**
   * Check if a specific endpoint is connected
   */
  isEndpointConnected(endpoint: string): boolean {
    const state = this.endpointStates.get(endpoint);
    return state?.value.status === 'connected' || false;
  }

  /**
   * Force reconnection for an endpoint
   */
  forceReconnect(endpoint: string): void {
    this.logger.info(`Force reconnecting to ${endpoint}`);

    const state = this.getEndpointState(endpoint);
    state.next({
      ...state.value,
      status: "reconnecting",
      reconnectAttempt: 0,
    });

    this.wsService.reconnect(endpoint);
  }

  /** Release one owner; the last owner also cancels HTTP and lifecycle streams. */
  disconnect(endpoint: string): void {
    this.wsService.disconnect(endpoint);
    const remaining = (this.endpointOwners.get(endpoint) ?? 1) - 1;
    if (remaining > 0) {
      this.endpointOwners.set(endpoint, remaining);
      return;
    }
    this.stopEndpoint(endpoint);
  }

  private stopEndpoint(endpoint: string): void {
    this.endpointStops.get(endpoint)?.next();
    this.endpointStops.get(endpoint)?.complete();
    this.endpointStops.delete(endpoint);
    this.endpointOwners.delete(endpoint);
    this.stopPollingFallback(endpoint);
    this.clearQueue(endpoint);
    const state = this.endpointStates.get(endpoint);
    if (state) {
      state.next({
        ...state.value,
        status: "disconnected",
        lastDisconnected: new Date(),
        usingPollingFallback: false,
      });
    }
    this.updateGlobalState();
  }

  /**
   * Get queued subscriptions for an endpoint
   */
  getQueuedSubscriptions(endpoint: string): QueuedSubscription[] {
    return this.subscriptionQueue.get(endpoint) || [];
  }

  /**
   * Clear queued subscriptions for an endpoint
   */
  clearQueue(endpoint: string): void {
    this.subscriptionQueue.delete(endpoint);
    this.updateQueuedCount();
  }

  // ============================================================================

  // Private Methods
  // ============================================================================

  private initializeEndpointState(endpoint: string): void {
    if (!this.endpointStates.has(endpoint)) {
      this.endpointStates.set(
        endpoint,
        new BehaviorSubject<WebSocketConnectionState>({
          status: "disconnected",
          lastConnected: null,
          lastDisconnected: null,
          reconnectAttempt: 0,
          maxReconnectAttempts: 10,
          error: null,
          usingPollingFallback: false,
          queuedSubscriptions: 0,
        }),
      );
    }
  }

  private getEndpointState(
    endpoint: string,
  ): BehaviorSubject<WebSocketConnectionState> {
    this.initializeEndpointState(endpoint);
    return this.endpointStates.get(endpoint)!;
  }

  private onConnectionSuccess(endpoint: string): void {
    const state = this.getEndpointState(endpoint);
    const now = new Date();

    state.next({
      ...state.value,
      status: "connected",
      lastConnected: now,
      reconnectAttempt: 0,
      error: null,
      usingPollingFallback: false,
    });

    this.connectionEvents$.next({
      type: "connected",
      endpoint,
      message: `Connected to ${endpoint}`,
    });

    // Stop any active polling fallback

    this.stopPollingFallback(endpoint);

    // Process queued subscriptions

    this.processQueuedSubscriptionsForEndpoint(endpoint);

    this.updateGlobalState();

    this.logger.info(`WebSocket connected: ${endpoint}`);
  }

  private onConnectionError(endpoint: string, error: unknown): void {
    const state = this.getEndpointState(endpoint);
    const errorMessage = error instanceof Error ? error.message : String(error);

    state.next({
      ...state.value,
      status: "error",
      error: errorMessage,
      lastDisconnected: new Date(),
    });

    this.connectionEvents$.next({
      type: "error",
      endpoint,
      message: `Connection error: ${errorMessage}`,
    });

    this.updateGlobalState();

    this.logger.error(`WebSocket error on ${endpoint}:`, {
      error: errorMessage,
    });
  }

  private syncEndpointState(
    endpoint: string,
    lifecycle: WebSocketLifecycle,
  ): void {
    const state = this.getEndpointState(endpoint);
    const polling = this.pollingFallbacks.has(endpoint);
    if (lifecycle.status === "connected" && (!polling || lifecycle.healthy)) {
      if (state.value.status !== "connected") {
        this.onConnectionSuccess(endpoint);
      }
      return;
    }
    state.next({
      ...state.value,
      status: polling ? "degraded" : lifecycle.status,
      reconnectAttempt: lifecycle.reconnectAttempt,
      maxReconnectAttempts: lifecycle.maxReconnectAttempts,
      lastDisconnected:
        lifecycle.status === "disconnected"
          ? new Date()
          : state.value.lastDisconnected,
      error: lifecycle.status === "error" ? "Connection failed" : null,
      usingPollingFallback: polling,
    });
    this.updateGlobalState();
  }

  private updateGlobalStateFromCoreStatus(
    status: "connecting" | "connected" | "disconnected" | "error",
  ): void {
    const currentState = this._globalState();

    switch (status) {
      case "connected":
        this._globalState.set({
          ...currentState,
          status: "connected",
          lastConnected: new Date(),
          error: null,
        });
        break;

      case "connecting":
        this._globalState.set({
          ...currentState,
          status: "connecting",
        });
        break;

      case "disconnected":
        this._globalState.set({
          ...currentState,
          status: "disconnected",
          lastDisconnected: new Date(),
        });
        break;

      case "error":
        this._globalState.set({
          ...currentState,
          status: "error",
          error: "Connection failed",
        });
        break;
    }
  }

  private updateGlobalState(): void {
    const allStates = Array.from(this.endpointStates.values()).map(
      (s) => s.value,
    );

    if (allStates.length === 0) {
      return;
    }

    // Determine aggregate status
    const hasConnected = allStates.some((s) => s.status === "connected");
    const hasError = allStates.some((s) => s.status === "error");
    const hasPolling = allStates.some((s) => s.usingPollingFallback);
    const isAllDisconnected = allStates.every(
      (s) => s.status === "disconnected",
    );
    const isAnyConnecting = allStates.some(
      (s) => s.status === "connecting" || s.status === "reconnecting",
    );

    let status: WebSocketConnectionState["status"] = "disconnected";

    if (hasPolling) {
      status = "degraded";
    } else if (hasConnected && !hasError) {
      status = hasPolling ? "degraded" : "connected";
    } else if (isAnyConnecting) {
      status = "connecting";
    } else if (hasError) {
      status = hasConnected ? "degraded" : "error";
    } else if (isAllDisconnected) {
      status = "disconnected";
    }

    const lastConnected =
      allStates
        .filter((s) => s.lastConnected)
        .map((s) => s.lastConnected!)
        .sort((a, b) => b.getTime() - a.getTime())[0] || null;

    const lastDisconnected =
      allStates
        .filter((s) => s.lastDisconnected)
        .map((s) => s.lastDisconnected!)
        .sort((a, b) => b.getTime() - a.getTime())[0] || null;

    const maxReconnectAttempt = Math.max(
      ...allStates.map((s) => s.reconnectAttempt),
      0,
    );

    const totalQueued = Array.from(this.subscriptionQueue.values()).reduce(
      (sum, queue) => sum + queue.length,
      0,
    );

    this._globalState.set({
      status,
      lastConnected,
      lastDisconnected,
      reconnectAttempt: maxReconnectAttempt,
      maxReconnectAttempts: 10,
      error: hasError ? "One or more connections failed" : null,
      usingPollingFallback: hasPolling,
      queuedSubscriptions: totalQueued,
    });
  }

  private updateQueuedCount(): void {
    const totalQueued = Array.from(this.subscriptionQueue.values()).reduce(
      (sum, queue) => sum + queue.length,
      0,
    );

    this._globalState.update((state) => ({
      ...state,
      queuedSubscriptions: totalQueued,
    }));
  }

  private processQueuedSubscriptions(): void {
    this.subscriptionQueue.forEach((_, endpoint) => {
      if (this.isEndpointConnected(endpoint)) {
        this.processQueuedSubscriptionsForEndpoint(endpoint);
      }
    });
  }

  private processQueuedSubscriptionsForEndpoint(endpoint: string): void {
    const queue = this.subscriptionQueue.get(endpoint);
    if (!queue || queue.length === 0) {
      return;
    }

    this.logger.info(
      `Processing ${queue.length} queued subscriptions for ${endpoint}`,
    );

    const failedItems: QueuedSubscription[] = [];

    for (const item of queue) {
      try {
        if (!this.wsService.send(endpoint, item.payload)) {
          failedItems.push(item);
          continue;
        }
        this.logger.debug(`Sent queued subscription to ${endpoint}`, {
          payload: item.payload,
        });
      } catch (error) {
        item.retryCount++;
        if (item.retryCount < 3) {
          failedItems.push(item);
        } else {
          this.logger.warn(`Dropped queued subscription after 3 retries`, {
            endpoint,
            payload: item.payload,
          });
        }
      }
    }

    if (failedItems.length > 0) {
      this.subscriptionQueue.set(endpoint, failedItems);
    } else {
      this.subscriptionQueue.delete(endpoint);
    }

    this.updateQueuedCount();
  }

  private startPollingFallback(
    endpoint: string,
    config: PollingFallbackConfig,
  ): Observable<WebSocketMessage> {
    const existing = this.pollingData$.get(endpoint);
    if (existing) {
      return existing;
    }
    this.logger.warn(`Starting polling fallback for ${endpoint}`, {
      interval: config.intervalMs,
    });

    const state = this.getEndpointState(endpoint);
    state.next({
      ...state.value,
      status: "degraded",
      usingPollingFallback: true,
    });

    this.connectionEvents$.next({
      type: "degraded",
      endpoint,
      message: `Using polling fallback for ${endpoint}`,
    });

    const stopPolling$ = new Subject<void>();
    this.pollingFallbacks.set(endpoint, stopPolling$);

    this.updateGlobalState();
    const stream$ = timer(0, config.intervalMs).pipe(
      switchMap(() =>
        this.http.get<unknown>(typeof config.endpoint === 'function' ? config.endpoint() : config.endpoint).pipe(
          retry({ count: config.maxRetries, delay: 1000 }),
          catchError((error) => {
            this.logger.error(`Polling fallback error for ${endpoint}:`, {
              error,
            });
            return EMPTY;
          }),
        ),
      ),
      map((data) => ({
        type: "market_update" as const,
        data,
        timestamp: new Date(),
        source: `polling:${endpoint}`,
      })),
      takeUntil(stopPolling$),
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        if (this.pollingFallbacks.get(endpoint) === stopPolling$) {
          this.pollingFallbacks.delete(endpoint);
          this.pollingData$.delete(endpoint);
        }
      }),
      share(),
    );
    this.pollingData$.set(endpoint, stream$);
    return stream$;
  }

  private stopPollingFallback(endpoint: string): void {
    const subject = this.pollingFallbacks.get(endpoint);
    if (subject) {
      subject.next();
      subject.complete();
      this.pollingFallbacks.delete(endpoint);
      this.pollingData$.delete(endpoint);
      this.logger.info(`Stopped polling fallback for ${endpoint}`);
    }
  }
}
